import * as React from "react"
import { Button as Btn, ButtonTableAction, Subtitle6 } from "@nova/component-typography"
import * as Icons from "@nova/component-icons"
import "./btn.css"
import { useTranslation } from "@nova/use-translation"
import { ConnectDragSource } from "react-dnd"
import * as Typography from "@nova/component-typography"
import { HoverTooltip } from "@nova/component-hover-tooltip"
import { NovaSpinner } from "@nova/component-nova-spinner"
import classNames from "classnames"

import type { JSX } from "react"

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>

export const Button = ({ className, children, ...rest }: ButtonProps) => (
    <Btn {...rest} className={classNames("button", "button--full", className)}>
        {children}
    </Btn>
)

export { ButtonSplit } from "./btn-split"

export { ButtonWorkspaceDropdown } from "./btn-workspace-dropdown"

export { ButtonWorkspaceUserDropdown, type UserContext } from "./btn-workspace-user-dropdown"

export { ButtonRadioDropdown } from "./btn-radio-dropdown"

export const ButtonPanelPrimary = ({ className, children, ...rest }: ButtonProps) => (
    <ButtonBar>
        <Btn
            {...rest}
            className={classNames("button", "button--full", className, "button-primary")}
        >
            {children}
        </Btn>
    </ButtonBar>
)

export const ButtonPanelSecondary = ({ className, children, ...rest }: ButtonProps) => (
    <ButtonBar>
        <button
            {...rest}
            className={classNames("button", className, "button-secondary", "typography__button")}
        >
            {children}
        </button>
    </ButtonBar>
)

export const ButtonFullLarge = ({ className, children, ...rest }: ButtonProps) => (
    <Button className={classNames("button-full__large", className)} {...rest}>
        {children}
    </Button>
)

/** @deprecated */
export const ButtonIcon = ({
    Icon,
    children,
    className,
    width,
    height,
    ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
    Icon: Icons.IconComponent
    width: number
    height: number
}) => {
    return (
        <button
            className={classNames(className, "select-none")}
            {...rest}
            style={{
                filter: rest.disabled ? "brightness(50%)" : undefined,
            }}
            disabled={rest.disabled}
        >
            <Icon className="mx-auto block" width={`${width}px`} height={`${height}px`} />
            {children}
        </button>
    )
}

export type ButtonBarProps = {
    className?: string
} & React.PropsWithChildren &
    React.HTMLAttributes<HTMLDivElement>

export const ButtonBar = ({ className, children, ...rest }: ButtonBarProps) => (
    <div className={classNames("button__bar", className)} {...rest}>
        {children}
    </div>
)

export const ButtonReturn = ({ className, children, ...rest }: ButtonProps) => (
    <Subtitle6 {...rest} as="button" className={classNames("button--return", className)}>
        <Icons.Return />
        <span className="button__label--return">{children}</span>
    </Subtitle6>
)

export const ButtonOutline = ({ className, children, ...rest }: ButtonProps) => (
    <Btn {...rest} className={classNames("button", "button--outline", className)}>
        {children}
    </Btn>
)

export type ButtonInlineProps = {
    label?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export const ButtonInline = ({ className, label, ...rest }: ButtonInlineProps) => {
    const { t } = useTranslation()
    return (
        <button
            {...rest}
            className={classNames("button", "button--inline", className)}
            data-text={label}
        >
            <span className="button__label">{t(label || "")}</span>
        </button>
    )
}

type ButtonInlineIconProps = {
    icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>
    iconClassName?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export const ButtonInlineIcon = ({
    icon: IconComponent,
    className,
    iconClassName: iconClassOverrides,
    ...rest
}: ButtonInlineIconProps) => {
    return (
        <button {...rest} className={classNames("button-inline-icon", className)}>
            <IconComponent className={iconClassOverrides} />
        </button>
    )
}

type ButtonTabInlineIconProps = {
    icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>
    iconClassName?: string
    iconAttributes?: React.SVGAttributes<SVGElement>
    active?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export const ButtonTabInlineIcon = ({
    icon: IconComponent,
    className,
    iconClassName: iconClassOverrides,
    iconAttributes,
    active,
    onClick,
    ...rest
}: ButtonTabInlineIconProps) => {
    return (
        <button
            {...rest}
            className={classNames(
                "h-6 w-6 rounded-sm hover:bg-[#4D4D4D] bg-transparent flex justify-center items-center cursor-pointer disabled:cursor-not-allowed",

                "group-hover:stroke-ws-accent",
                active && "bg-[#4d4d4d]",
                className
            )}
            onClick={(e) => {
                e.stopPropagation()
                onClick?.(e)
            }}
        >
            <IconComponent
                className={classNames(
                    `text-[18px] !m-0`,
                    { "!stroke-[#808080]": rest.disabled },
                    iconClassOverrides
                )}
                {...iconAttributes}
            />
        </button>
    )
}
export const ButtonUnderline = ({
    className,
    children,
    onClick,
    selected,
    setSelected,
    ...rest
}: ButtonProps & { selected: boolean; setSelected: () => void; onClick?: () => void }) => {
    return (
        <button
            className={classNames(className, "button--underline", {
                "button--underline-selected": selected,
            })}
            onClick={() => {
                setSelected()
                onClick?.()
            }}
            {...rest}
        >
            {children}
        </button>
    )
}

export const ButtonInvite = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button className={classNames(className, "button", "button--invite")} {...rest}>
            {children}
        </button>
    )
}

type ButtonMemberSeatProps = {
    dragRef?: ConnectDragSource
} & ButtonProps
export const ButtonMemberSeat = ({
    className,
    children,
    dragRef,
    ...rest
}: ButtonMemberSeatProps) => {
    const ref = React.useRef<HTMLButtonElement>(null)
    dragRef?.(ref)
    return (
        <button ref={ref} className={classNames(className, "button--member-seat")} {...rest}>
            {children}
        </button>
    )
}

export const ButtonTab = ({
    buttons,
    selected,
    setSelected,
    className,
}: React.HTMLAttributes<HTMLDivElement> & {
    buttons: Record<string, JSX.Element>
    selected: string
    setSelected: (selected: string) => void
}) => {
    const names = Object.keys(buttons)
    return (
        <>
            <ButtonBar style={{ gap: 0 }} className={classNames(className)}>
                {names.map((name, idx) => {
                    return (
                        <ButtonUnderline
                            style={{ width: "100%" }}
                            selected={selected === name}
                            setSelected={() => setSelected(name)}
                            key={idx}
                        >
                            {name}
                        </ButtonUnderline>
                    )
                })}
            </ButtonBar>
            {buttons[selected]}
        </>
    )
}

type ButtonLayerControlProps = {
    isLayerVisible: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export const ButtonLayerControl = ({
    isLayerVisible,
    className,
    children,
    ...rest
}: ButtonLayerControlProps) => {
    return (
        <Btn
            {...rest}
            data-tid="hovered-layer-visiblity-button"
            className={classNames(
                "button-layer-control",
                { "button-layer-control--visible": isLayerVisible },
                className
            )}
        >
            {children}
        </Btn>
    )
}

type ButtonVisibilityZoomProps = {
    isActivated: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export const ButtonVisibilityZoom = ({
    isActivated,
    className,
    ...rest
}: ButtonVisibilityZoomProps) => {
    return (
        <button
            {...rest}
            data-tid="hovered-zoom-button"
            className={classNames(
                "button-visibility-zoom",
                {
                    "button-visibility-zoom--visible": isActivated,
                },
                className
            )}
        >
            <Icons.Center className="button-visibility-zoom-icon" />
        </button>
    )
}

export const ButtonPerimeterAction = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <ButtonTableAction {...rest} className={classNames(className, "historical-view__button")}>
            {children}
        </ButtonTableAction>
    )
}
export const ButtonPerimeterActionSmall = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <ButtonTableAction
            {...rest}
            className={classNames(className, "historical-view__button-small")}
        >
            {children}
        </ButtonTableAction>
    )
}

export const ButtonMapIcon = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button className={classNames("button--map-icon", className)} {...rest}>
            {children}
        </button>
    )
}

export const ButtonAccordion = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button
            data-tid="hovered-layer-accordion-button"
            {...rest}
            className={classNames(className, "button--accordion")}
        >
            {children}
        </button>
    )
}

export const ButtonPluginPanelControl = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button className={classNames("button--map-control-icons-center", className)} {...rest}>
            {children}
        </button>
    )
}

export const ButtonHistoricalViewSVG = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button {...rest} className={classNames("button--historical-view__svg-button", className)}>
            {children}
        </button>
    )
}

export const ButtonPhotoSelectorTile = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button {...rest} className={classNames("button--photo-selector__tile", className)}>
            {children}
        </button>
    )
}

export const ButtonPluginSelector = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button {...rest} className={classNames("button--plugin-selector", className)}>
            {children}
        </button>
    )
}

export const ButtonPortfolioTreeCard = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button {...rest} className={classNames("button--portfolio-tree__card", className)}>
            {children}
        </button>
    )
}

type RadioButtonProps = {
    checked: boolean
} & ButtonProps

export const ButtonRadioInput = ({ checked, className, ...rest }: RadioButtonProps) => {
    return (
        <button
            {...rest}
            role="radio"
            aria-checked={checked}
            className={classNames("button--radio-input", className)}
        />
    )
}

export const ButtonPortfolioBoardSidebar = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button {...rest} className={classNames("button--portfolio-board-sidebar", className)}>
            {children}
        </button>
    )
}

type ButtonTooltipOptionProps = {
    icon: React.ReactElement
    title: string
} & ButtonProps
export const ButtonTooltipOption = ({
    icon,
    title,
    className,
    onClick,
    ...rest
}: ButtonTooltipOptionProps) => {
    const { t } = useTranslation()
    return (
        <button
            className={classNames("button--tooltip-option", className)}
            onClick={onClick}
            {...rest}
        >
            {icon}
            <Typography.P>{t(title)}</Typography.P>
        </button>
    )
}

export const ButtonZoom = ({ className, name, onClick, ...rest }: ButtonProps) => {
    return (
        <ButtonBar>
            <button
                data-tid="hovered-zoom-button"
                {...rest}
                className={classNames("button--zoom-button", "button", className)}
                onClick={onClick}
            >
                {name}&nbsp;
                <Icons.CenterSVG style={{ display: "inline", marginBottom: 2 }} />
            </button>
        </ButtonBar>
    )
}

export const ButtonNovaPrimary = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button
            className={classNames("button--nova-primary-button", "button", className)}
            {...rest}
        >
            {children}
        </button>
    )
}

type ButtonFederatedProps = {
    Icon: Icons.IconComponent
}

export const ButtonFederated = ({ Icon }: ButtonFederatedProps) => {
    return (
        <button
            type="button"
            className="bg-ivory hover:border hover:border-night rounded-xl h-[50px] w-[50px] gap-4 flex items-center justify-center"
        >
            <Icons.Icon Icon={Icon} />
        </button>
    )
}

export const ButtonSeats = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button
            {...rest}
            className={classNames(
                "bg-nova-button-gray font-semibold py-2 px-3 rounded-md text-nova-soft-white mb-1",
                className
            )}
        >
            {children}
        </button>
    )
}

export const NeomorphicActionButton = ({ className, children, ...rest }: ButtonProps) => {
    return (
        <button {...rest} className={classNames("neomorphic-action-button", className)}>
            {children}
        </button>
    )
}

type LayerDetailsPrimaryButtonProps = {
    icon: Icons.IconComponent
    title: string
} & ButtonProps

export const ButtonLayerDetailsPrimary = ({
    icon: Icon,
    title,
    className,
    ...rest
}: LayerDetailsPrimaryButtonProps) => {
    const { t } = useTranslation()
    const datatid = `${title.toLowerCase().replace(/\s+/g, "-")}-primary-button`
    return (
        <button
            data-tid={datatid}
            {...rest}
            className={classNames("button__layer-details-primary", className)}
        >
            <Icons.SmallIcon Icon={Icon} />
            <div>{t(title)}</div>
        </button>
    )
}

export const ButtonLayerDetailsSecondary = ({
    title,
    className,
    children,
    ...rest
}: { title: string } & ButtonProps) => {
    const { t } = useTranslation()
    const datatid = `${title.toLowerCase().replace(/\s+/g, "-")}-secondary-button`
    return (
        <button
            data-tid={datatid}
            {...rest}
            className={classNames("button__layer-details-secondary", className)}
        >
            {children}
            {t(title)}
        </button>
    )
}

type PluginButtonProps = {
    name?: string
    displayName?: string
    icon: React.FunctionComponent<React.SVGAttributes<SVGElement>>
    selected?: boolean
    isPanelVisible?: boolean
    setSelectedPlugin?: (name?: string) => void
    className?: string
    disabled?: boolean
}
export const PluginButton = ({
    name,
    displayName,
    selected,
    icon: Icon,
    setSelectedPlugin,
    className,
    disabled,
}: PluginButtonProps) => {
    const pluginTooltipRef = React.useRef<HTMLDivElement>(null)
    return (
        <div
            className={classNames("bottom-panel-shell__plugin cursor-pointer", className)}
            data-testid="side-panel-layers-plugin"
            data-tid={
                name ? `create-${name.toLowerCase().replace(/\s+/g, "-")}-layer-button` : undefined
            }
        >
            <div
                ref={pluginTooltipRef}
                data-testid={selected ? "selected-action" : "unselected-action"}
                data-name={name}
                className={classNames("bottom-panel-shell__plugin-container", {
                    "bottom-panel-shell__plugin--selected": selected,
                    "!cursor-not-allowed": disabled,
                })}
                onClick={(e) => {
                    if (disabled || !name) return

                    // Prevent ClickAwayListener from handling this click.
                    e.stopPropagation()

                    if (selected) setSelectedPlugin?.(undefined)
                    else setSelectedPlugin?.(name)
                }}
            >
                <Icon />
                {displayName && (
                    <HoverTooltip
                        id="plugin-tooltip"
                        containerClassName="bottom-panel-shell__plugin-tooltip"
                        parentRef={pluginTooltipRef}
                        style={{
                            textAlign: "center",
                            color: "black",
                            padding: "8px",
                            fontSize: "12px",
                        }}
                        borderColor="#00000026"
                        hoverTimeoutDelay={0}
                        hoverStartDelay={1000}
                    >
                        {displayName}
                    </HoverTooltip>
                )}
            </div>
        </div>
    )
}

export const ButtonPDFTemplatePrimary = ({ className, children, ...rest }: ButtonProps) => (
    <button
        {...rest}
        className={classNames("button-pdf-template button--full basis-full", className)}
    >
        {children}
    </button>
)

export const ButtonPDFTemplateSecondary = ({ className, children, ...rest }: ButtonProps) => (
    <button
        {...rest}
        className={classNames(
            "border-2 disabled:border-2 border-[#B0B0B0] button-pdf-template button--secondary basis-full",
            className
        )}
    >
        {children}
    </button>
)

/**
 *
 * THIS POINT DOWN HAS BUTTONS BROUGHT IN BY THE "UI NEXT STEPS"
 */

const buttonClasses = ({ LeftIcon, RightIcon, clickWillNavigate, isRounded }: NovaButtonProps) => {
    return classNames(
        "py-1.5 flex items-center select-none min-w-[4.25rem] gap-1 relative group/button",

        {
            "rounded-lg": isRounded,
            "justify-center": !clickWillNavigate,
            "justify-start": clickWillNavigate,
            "pl-3": !LeftIcon || clickWillNavigate,
            "pl-2": !!LeftIcon && !clickWillNavigate,
            "pr-3": !RightIcon || clickWillNavigate,
            "pr-2": !!RightIcon && !clickWillNavigate,
            // "gap-1": !!title,
        }
    )
}

export enum ButtonStatus {
    Normal,
    Loading,
    Succeeded,
    Failed,
    Selected,
}

type NovaSpinnerColors = {
    background: string
    primary: string
}

export type NovaButtonProps = {
    LeftIcon?: Icons.IconComponent
    leftIconClassName?: string
    title?: string
    succeededTitle?: string
    failedTitle?: string
    RightIcon?: Icons.IconComponent
    rightIconClassName?: string
    badge?: React.ReactNode

    /**
     * Value should be in rem.
     * @example "1.5rem"
     * */
    height?: string

    status?: ButtonStatus
    isLoading?: boolean // deprecated, use `status` on all Buttons instead.
    spinnerColors?: NovaSpinnerColors
    preserveWidthWhileLoading?: boolean
    isReadOnly?: boolean
    clickWillNavigate?: boolean
    isDestructive?: boolean
    /**
     * Whether bg/text/border colors should be overridden
     * by the provided className.
     * @default false
     */
    overrideColors?: boolean
    /** @default true */
    isRounded?: boolean
    isTransparent?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

const InnerButtonNavigationArrow = ({
    isHovering,
    isActive,
}: {
    isHovering: boolean
    isActive: boolean
}) => {
    return (
        <>
            {/* Spacer */}
            <div className="flex-grow" />

            <Icons.SmallIcon
                Icon={Icons.ArrowRight}
                className={classNames({
                    hidden: !isHovering && !isActive,
                    "text-blue-200": isHovering && !isActive,
                    "text-blue-500": isActive,
                })}
            />
        </>
    )
}

const StandardButton = ({
    LeftIcon,
    leftIconClassName,
    title,
    succeededTitle,
    failedTitle,
    RightIcon,
    rightIconClassName,
    badge,
    height,
    clickWillNavigate,
    status = ButtonStatus.Normal,
    spinnerColors,
    preserveWidthWhileLoading,
    isRounded = true,
    generateClasses,
    ...rest
}: NovaButtonProps & {
    generateClasses: ({
        ...props
    }: NovaButtonProps & {
        hasSucceeded: boolean
        hasFailed: boolean
        isSelected: boolean
    }) => string
}) => {
    const [isHovering, setIsHovering] = React.useState(false)
    const [isActive, setIsActive] = React.useState(false)
    const [persistentStatus, setPersistentStatus] = React.useState(status)

    React.useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | undefined

        setPersistentStatus(status)

        if (status === ButtonStatus.Succeeded || status === ButtonStatus.Failed) {
            timeout = setTimeout(() => setPersistentStatus(ButtonStatus.Normal), 6000)
        }

        return () => {
            clearTimeout(timeout)
        }
    }, [status])

    const isLoading = persistentStatus === ButtonStatus.Loading
    const isSelected = persistentStatus === ButtonStatus.Selected
    const hasSucceeded = persistentStatus === ButtonStatus.Succeeded
    const hasFailed = persistentStatus === ButtonStatus.Failed

    return (
        <button
            disabled={
                rest.disabled ||
                (persistentStatus !== ButtonStatus.Normal &&
                    persistentStatus !== ButtonStatus.Selected)
            }
            className={classNames(
                rest.className,
                generateClasses({ ...rest, height, hasSucceeded, hasFailed, isSelected }),
                buttonClasses({ LeftIcon, RightIcon, clickWillNavigate, isRounded })
            )}
            style={
                height
                    ? {
                          height,
                      }
                    : undefined
            }
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            onClick={rest.onClick}
        >
            {LeftIcon && (
                <Icons.SmallIcon
                    Icon={LeftIcon}
                    className={classNames(leftIconClassName, { "opacity-0": isLoading })}
                />
            )}
            {title &&
                (persistentStatus === ButtonStatus.Normal ||
                    persistentStatus === ButtonStatus.Selected ||
                    (isLoading && preserveWidthWhileLoading)) && (
                    <h5
                        className={classNames("min-h-5 h-fit", {
                            "opacity-0": isLoading,
                        })}
                    >
                        {title}
                    </h5>
                )}
            {badge && !hasSucceeded && !hasFailed && badge}
            {succeededTitle && hasSucceeded && <h5 className="h-5">{succeededTitle}</h5>}
            {failedTitle && hasFailed && <h5 className="h-5">{failedTitle}</h5>}
            {!hasSucceeded && !hasFailed && RightIcon && (
                <Icons.SmallIcon
                    Icon={RightIcon}
                    className={classNames(rightIconClassName, { "opacity-0": isLoading })}
                />
            )}
            {hasSucceeded && <Icons.SmallIcon Icon={Icons.Checkmark} className="text-green-500" />}
            {hasFailed && <Icons.SmallIcon Icon={Icons.Error} className="text-red-500" />}
            {isLoading && (
                <NovaSpinner
                    width={20}
                    height={20}
                    className={classNames("absolute", {
                        "inset-0 m-auto": preserveWidthWhileLoading,
                    })}
                    fill={spinnerColors?.primary || "#B1B1B3"}
                    color={spinnerColors?.background || "#0C0C0F"}
                /> // gray-100, gray-900
            )}
            {clickWillNavigate && (
                <InnerButtonNavigationArrow isHovering={isHovering} isActive={isActive} />
            )}
        </button>
    )
}

export const ButtonBorderless = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={() =>
                classNames(
                    "h-8 p-px",
                    {
                        "text-gray-100 disabled:text-gray-500": !props.overrideColors,
                        "hover:text-gray-0 active:text-gray-300":
                            !props.isDestructive && !props.overrideColors,
                        "hover:text-red-500 active:text-red-200":
                            props.isDestructive && !props.overrideColors,
                        "cursor-not-allowed": props.disabled,
                        "h-8": !props.height,
                    },
                    props.className
                )
            }
        />
    )
}

export const ButtonTertiary = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={({ hasSucceeded, hasFailed, isSelected }) =>
                classNames(
                    props.className,
                    "border",
                    { "border-gray-500 text-gray-100": !hasSucceeded && !hasFailed && !isSelected },
                    { "text-gray-0": hasSucceeded || hasFailed },
                    { "border-green-500": hasSucceeded },
                    { "border-red-500": hasFailed },
                    {
                        "hover:border-gray-300 hover:text-gray-0":
                            !props.isReadOnly && !hasSucceeded && !hasFailed && !isSelected,
                    },
                    {
                        "active:border-gray-300 active:text-gray-300":
                            !hasSucceeded && !hasFailed && !isSelected,
                    },
                    {
                        "disabled:border-gray-500 disabled:text-gray-500":
                            !hasSucceeded && !hasFailed,
                    },
                    {
                        "hover:border-gray-500 hover:text-gray-100 cursor-default":
                            props.isReadOnly,
                    },
                    {
                        "bg-gray-500 border-white-100 text-gray-0": isSelected,
                    },
                    { "h-8": !props.height },
                    { "cursor-not-allowed": props.disabled }
                )
            }
        />
    )
}

export const ButtonQuaternary = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={({ hasSucceeded, hasFailed, isSelected }) =>
                classNames(
                    props.className,
                    "border",
                    {
                        "border-transparent text-gray-0":
                            !hasSucceeded && !hasFailed && !isSelected,
                    },
                    { "text-gray-0": hasSucceeded || hasFailed },
                    { "border-green-500": hasSucceeded },
                    { "border-red-500": hasFailed },
                    {
                        "hover:border-gray-500 hover:text-gray-0":
                            !props.isReadOnly && !hasSucceeded && !hasFailed && !isSelected,
                    },
                    {
                        "active:border-gray-500 active:text-gray-0 active:bg-gray-500":
                            !hasSucceeded && !hasFailed && !isSelected,
                    },
                    {
                        "disabled:border-gray-500 disabled:text-gray-500":
                            !hasSucceeded && !hasFailed,
                    },
                    {
                        "hover:border-gray-500 hover:text-gray-0 cursor-default": props.isReadOnly,
                    },
                    {
                        "bg-gray-500 border-white-100 text-gray-0": isSelected,
                    },
                    { "h-8": !props.height },
                    { "cursor-not-allowed": props.disabled }
                )
            }
        />
    )
}

export const ButtonTertiarySquare = ({
    Icon,
    isLoading,
    size,
    ...rest
}: Pick<NovaButtonProps, "isLoading" | "disabled"> & {
    size?: number
    Icon: Icons.IconComponent
} & React.HTMLAttributes<HTMLButtonElement>) => {
    return (
        <button
            disabled={rest.disabled || isLoading}
            {...rest}
            className={classNames(
                "p-1.5 flex items-center justify-center rounded-lg select-none",
                rest.className,
                "border border-gray-500 text-gray-100",
                "hover:border-gray-300 hover:text-gray-0",
                "active:border-gray-300 active:text-gray-300",
                "disabled:border-gray-500 disabled:text-gray-500",
                { "w-8 h-8": !size },
                { "cursor-not-allowed": rest.disabled }
            )}
            style={
                size
                    ? {
                          width: size,
                          height: size,
                      }
                    : undefined
            }
        >
            <Icons.SmallIcon Icon={Icon} className={classNames({ "opacity-0": isLoading })} />
            {isLoading && (
                <NovaSpinner
                    width={20}
                    height={20}
                    className="absolute ml-1"
                    fill="#B1B1B3"
                    color="#0C0C0F"
                /> // gray-100, gray-900
            )}
        </button>
    )
}

export const ButtonSecondary = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={({ isSelected, height, isReadOnly, disabled }) =>
                classNames(
                    props.className,
                    "bg-gray-700",
                    {
                        "hover:bg-gray-500 hover:shadow-button-hover": !isReadOnly,
                    },
                    {
                        "border-y border-y-transparent hover:border-t-white-200 text-gray-100 hover:text-gray-100":
                            !isSelected,
                        "border border-blue-500 text-gray-0": isSelected,
                    },
                    "active:bg-gray-700 active:text-gray-300 active:shadow-button-active active:border-0",
                    "disabled:border-gray-700 disabled:text-gray-500 disabled:pointer-events-none",
                    { "hover:bg-gray-700 hover:text-gray-100 cursor-default": isReadOnly },
                    { "h-8": !height },
                    { "cursor-not-allowed": disabled }
                )
            }
        />
    )
}

export const ButtonFloating = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={({ height, isReadOnly }) =>
                classNames(
                    props.className,
                    "bg-black-900 text-gray-300 border border-gray-500 transition-all duration-100",
                    {
                        "hover:text-gray-0": !isReadOnly,
                        "h-8": !height,
                    }
                )
            }
        />
    )
}

export const ButtonPrimary = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={({ height, disabled }) =>
                classNames(
                    "bg-blue-500 text-gray-900 border-y border-b-black-300 border-t-transparent",
                    "hover:bg-blue-200 hover:shadow-button-hover hover:border-t-white-200",
                    "active:bg-blue-500 active:shadow-button-active active:border-0",
                    "disabled:bg-blue-700 disabled:text-white-300 disabled:cursor-not-allowed disabled:pointer-events-none",
                    height ?? "h-8",
                    { "cursor-not-allowed": disabled }
                )
            }
        />
    )
}

export const ButtonDestructive = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={({ height, disabled }) =>
                classNames(
                    props.className,
                    "text-red-200 border border-red-700",
                    { "bg-red-900": !props.isTransparent },
                    { "bg-transparent": props.isTransparent },
                    "hover:bg-red-700 hover:text-grey-0 hover:shadow-button-hover",
                    "active:bg-red-900 hover:text-grey-0 active:shadow-button-active active:border-transparent",
                    "disabled:bg-black-800 disabled:text-white-300 disabled:border-white-100 disabled:cursor-not-allowed disabled:pointer-events-none",
                    { "h-8": !height },
                    { "cursor-not-allowed": disabled }
                )
            }
        />
    )
}

export const ButtonAlternate = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            generateClasses={({ height, disabled }) =>
                classNames(
                    props.className,
                    "text-orange-200 border border-orange-700",
                    { "bg-orange-900": !props.isTransparent },
                    { "bg-transparent": props.isTransparent },
                    "hover:bg-orange-700 hover:text-grey-0 hover:shadow-button-hover",
                    "active:bg-orange-900 active:text-grey-0 active:shadow-button-active active:border-transparent",
                    "disabled:bg-black-800 disabled:text-white-300 disabled:border-white-100 disabled:cursor-not-allowed disabled:pointer-events-none",
                    { "h-8": !height },
                    { "cursor-not-allowed": disabled }
                )
            }
        />
    )
}

export const ButtonRemove = (props: NovaButtonProps & { isRemoving: boolean }) => {
    const { isRemoving } = props
    return <ButtonDestructive {...props} title={isRemoving ? "Removing..." : props.title} />
}

export const ButtonSwitcher = ({
    primaryLabel,
    secondaryLabel,
    onPrimaryClick,
    onSecondaryClick,
}: {
    primaryLabel: string
    secondaryLabel: string
    onPrimaryClick: () => void
    onSecondaryClick: () => void
} & React.HTMLAttributes<HTMLButtonElement>) => {
    const [isSwitched, setIsSwitched] = React.useState(false)

    const handlePrimaryClick = () => {
        setIsSwitched(false)
        onPrimaryClick()
    }
    const handleSecondaryClick = () => {
        setIsSwitched(true)
        onSecondaryClick()
    }

    return (
        <div className="flex border border-gray-500 rounded-lg select-none">
            <button
                onClick={handlePrimaryClick}
                className={classNames("py-2 px-3 rounded-md", {
                    "bg-transparent text-gray-300": isSwitched,
                    "text-gray-50 font-medium bg-gray-500": !isSwitched,
                })}
            >
                {primaryLabel}
            </button>
            <button
                onClick={handleSecondaryClick}
                className={classNames("py-2 px-3 rounded-md", {
                    "bg-transparent text-gray-300": !isSwitched,
                    "text-gray-50 font-medium bg-gray-500": isSwitched,
                })}
            >
                {secondaryLabel}
            </button>
        </div>
    )
}

type ButtonToggleProps = {
    /** Optionally pass an initial state of the toggle */
    initialValue?: boolean
    /** Callback that gets called each time toggle changes state */
    onToggle?: (isOn: boolean) => void
} & React.HTMLAttributes<HTMLButtonElement>

export const ButtonToggle = ({ initialValue = false, onToggle }: ButtonToggleProps) => {
    const [isOn, setIsOn] = React.useState(initialValue)

    const handleClick = () => {
        setIsOn((prev) => !prev)
        onToggle?.(!isOn)
    }

    return (
        <button className={`toggle-button ${isOn ? "on" : ""}`} onClick={handleClick}>
            <div className="toggle-circle" />
        </button>
    )
}

type TranslucentButtonProps = {
    Icon: Icons.IconComponent
    title?: string
    isActive?: boolean
    isLight?: boolean
} & React.ButtonHTMLAttributes<HTMLButtonElement>

export const ButtonTranslucent = ({
    Icon,
    title,
    isActive = false,
    isLight = false,
    disabled,
    ...rest
}: TranslucentButtonProps) => {
    const styles = {
        dark: {
            base: "border-white-300 bg-black-600 hover:border-white-400 hover:bg-gray-900",
            active: "border-blue-500 bg-gray-900",
            disabled: "disabled:border-gray-700 disabled:bg-black-800",

            iconBase: "text-white-800 hover:text-gray-0 disabled:text-white-400",
            iconActive: "text-gray-0",

            title: "text-gray-0",
        },
        light: {
            base: "border-black-300 bg-white-500 hover:border-black-400 hover:bg-white-1000",
            active: "border-blue-500 bg-white-1000",
            disabled: "disabled:border-black-300 disabled:bg-white-600",

            iconBase: "text-black-600 hover:text-gray-900 disabled:text-black-400",
            iconActive: "text-gray-900",

            title: "text-gray-900",
        },
    }

    const currentStyle = styles[isLight ? "light" : "dark"]

    return (
        <button
            disabled={disabled}
            className={classNames(
                "h-9 flex items-center justify-center gap-1 border rounded-[1.125rem]",
                title ? "px-3" : "px-2",

                "disabled:cursor-not-allowed disabled:opacity-60",

                currentStyle.disabled,

                isActive ? currentStyle.active : currentStyle.base
            )}
            {...rest}
        >
            <Icons.SmallIcon
                Icon={Icon}
                className={classNames(currentStyle.iconBase, {
                    [currentStyle.iconActive]: isActive,
                })}
            />
            {title && <small className={currentStyle.title}>{title}</small>}
        </button>
    )
}

export const ButtonCreate = ({
    onClick,
    ...rest
}: { onClick: () => void } & React.HTMLAttributes<HTMLButtonElement>) => {
    const [isHovered, setIsHovered] = React.useState(false)
    const [isActive, setIsActive] = React.useState(false)
    const gradient =
        "linear-gradient(84deg, rgba(0, 175, 255, 0.50) 0%, rgba(241, 244, 60, 0.50) 108.17%)"
    const gradientHovered =
        "linear-gradient(84deg, rgba(0, 175, 255, 0.90) 0%, rgba(241, 244, 60, 0.90) 108.17%)"
    const [showSpin, setShowSpin] = React.useState(false)
    React.useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null
        if (isHovered) {
            timer = setTimeout(() => setShowSpin(true), 150)
        } else {
            setShowSpin(false)
        }
        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [isHovered])

    return (
        <div
            className={classNames("relative inline-flex rounded-full overflow-hidden", {
                "p-px": isActive,
                "p-[2px]": !isActive,
            })}
            style={{ backgroundImage: gradient }}
        >
            <div
                aria-hidden
                className="absolute inset-0 rounded-full pointer-events-none transition-opacity duration-150"
                style={{
                    backgroundImage: gradientHovered,
                    opacity: isHovered ? 1 : 0,
                    zIndex: 0,
                }}
            />
            {showSpin && (
                <div
                    aria-hidden
                    className={classNames(
                        "absolute -inset-10 rounded-full pointer-events-none animate-spin-create-button"
                    )}
                    style={{
                        background:
                            "conic-gradient(from 0deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.5) 20deg, rgba(255,255,255,0) 60deg, rgba(255,255,255,0) 360deg)",
                        zIndex: 1,
                    }}
                />
            )}
            <button
                {...rest}
                onMouseDown={() => setIsActive(true)}
                onMouseUp={() => setIsActive(false)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={onClick}
                className={classNames(
                    "relative z-10 rounded-full",
                    {
                        "px-2 py-[6px]": !isActive,
                        "px-[9px] py-[7px]": isActive,
                    },
                    "text-gray-0 font-medium",
                    "flex gap-1 items-center overflow-hidden",
                    rest.className
                )}
            >
                <div className="absolute inset-0 rounded-full bg-gray-800" />
                <div
                    className="absolute inset-0 rounded-full bg-blue-510 transition-opacity duration-150"
                    style={{ opacity: isHovered || isActive ? 1 : 0 }}
                />
                <div className="relative flex gap-1 px-2 items-center">
                    <Icons.SmallIcon
                        Icon={Icons.Plus}
                        iconClassName={classNames({
                            "text-white-500": !isHovered && !isActive,
                            "text-white-1000": isHovered || isActive,
                        })}
                    />
                    <h5 className="m-0">Create</h5>
                </div>
            </button>
        </div>
    )
}

export const ButtonNavigationBack = (rest: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    return (
        <button
            disabled={rest.disabled}
            {...rest}
            className={classNames(
                rest.className,
                "py-1.5 flex items-center justify-center select-none gap-3 relative",
                "group/button",
                "h-5 p-px text-gray-100",
                "disabled:text-gray-500",
                "hover:text-gray-0 active:text-gray-300",
                {
                    "cursor-not-allowed": rest.disabled,
                }
            )}
        >
            <Icons.SmallIcon
                Icon={Icons.ArrowLeft}
                className="group-hover/button:text-blue-500 group-active/button:text-blue-700 group-disabled/button:text-gray-500"
            />
            {rest.title && <h5 className="h-5">{rest.title}</h5>}
        </button>
    )
}

export const ButtonAddFiles = ({ onClick, ...rest }: React.HTMLAttributes<HTMLButtonElement>) => {
    return (
        <button
            {...rest}
            className={classNames(
                "h-10 w-full rounded-lg border flex items-center justify-center select-none",
                "group text-white-1000",
                "border-dashed border-white-300 hover:border-white-1000",
                "bg-transparent",
                rest.className
            )}
            onClick={onClick}
        >
            <div className="flex gap-2">
                <Icons.SmallIcon Icon={Icons.Backup} />
                <h5 className="group-hover:text-blue-500">Add More Files (WIP)</h5>
            </div>
        </button>
    )
}

export const ButtonDynamic = (
    props: NovaButtonProps & { isPrimary?: boolean; isSecondary?: boolean; isTertiary?: boolean }
) => {
    const { isPrimary, isSecondary, isTertiary } = props
    if (isPrimary) {
        return <ButtonPrimary {...props} />
    }
    if (isSecondary) {
        return <ButtonSecondary {...props} />
    }
    if (isTertiary) {
        return <ButtonTertiary {...props} />
    }
    return <ButtonPrimary {...props} />
}

export const ButtonAuthenticationPrimary = (props: NovaButtonProps) => {
    return (
        <StandardButton
            {...props}
            height={"50px"}
            RightIcon={Icons.ArrowRight}
            generateClasses={() =>
                classNames(
                    props.className,
                    "bg-lazeryellow text-darkmoon uppercase tracking-[1.4px] leading-[34px] font-medium mono",
                    "hover:bg-offwhite hover:border-night hover:border-solid hover:border",
                    "disabled:bg-lazeryellow/50 disabled:text-black-600 disabled:cursor-not-allowed disabled:pointer-events-none"
                )
            }
        />
    )
}

export const ButtonAuthenticationSecondary = (props: NovaButtonProps & { hideIcon?: boolean }) => {
    return (
        <StandardButton
            {...props}
            height={"50px"}
            RightIcon={!props.hideIcon ? Icons.ArrowRight : undefined}
            generateClasses={({ disabled }) =>
                classNames(
                    props.className,
                    "bg-offwhite text-black-900 uppercase tracking-[1.4px] leading-[34px] font-medium mono border-black-900 border-solid border",
                    "disabled:text-black-600 disabled:cursor-not-allowed disabled:pointer-events-none disabled:border-black-600",
                    { "cursor-not-allowed": disabled }
                )
            }
        />
    )
}

export const ButtonAuthenticationTertiary = (props: NovaButtonProps & { color: string }) => {
    return (
        <button
            {...props}
            className="text-[14px] bg-transparent p-0 leading-[24px] underline decoration-solid decoration-auto underline-offset-auto hover:!text-[#406354]"
            style={{ color: props.color }}
        >
            {props.title}
        </button>
    )
}

export const RadioButton = ({
    isSelected,
    onClick,
    size = "normal",
}: {
    isSelected: boolean
    onClick: () => void
    size?: "normal" | "small"
}) => {
    const [isHovering, setIsHovering] = React.useState(false)
    const [isPressed, setIsPressed] = React.useState(false)

    return (
        <div
            className={classNames(
                "w-6 h-6 rounded-full border flex items-center justify-center cursor-pointer",
                {
                    "border-gray-100": !isSelected && (isPressed || !isHovering),
                    "border-gray-50": isSelected && !isPressed && !isHovering,
                    "border-gray-0": isHovering && !isPressed,
                    "group-hover:border-gray-0": !isPressed,
                    "border-gray-300": isSelected && isPressed,
                    "bg-gray-800": !isSelected,
                    "bg-gray-700": isSelected,
                    "w-6 h-6": size === "normal",
                    "w-4 h-4": size === "small",
                }
            )}
            onClick={onClick}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => {
                setIsHovering(false)
                setIsPressed(false)
            }}
            onMouseDown={() => setIsPressed(true)}
            onMouseUp={() => setIsPressed(false)}
        >
            {isSelected && (
                <div
                    className={classNames(
                        "rounded-full bg-blue-500 group-hover:drop-shadow-radio-button",
                        {
                            "drop-shadow-radio-button": isHovering,
                            "w-2.5 h-2.5": size === "normal",
                            "w-[0.4375rem] h-[0.4375rem]": size === "small",
                        }
                    )}
                />
            )}
        </div>
    )
}

export const IconButton: React.FunctionComponent<
    React.PropsWithChildren<React.HTMLProps<HTMLButtonElement>>
> = ({ children, className, ...rest }) => {
    return (
        <button
            //@ts-expect-error cant figure out why typescript doesn't like this type here
            type="button"
            className={classNames("hover:text-white disabled:text-gray-500", className)}
            {...(rest as React.HTMLProps<HTMLButtonElement>)}
        >
            {children}
        </button>
    )
}

export const ListItemButton: React.FunctionComponent<
    React.PropsWithChildren<React.HTMLProps<HTMLButtonElement>> & {
        LeftIcon?: Icons.IconComponent
        RightIcon?: Icons.IconComponent
        active?: boolean
    }
> = ({ children, className, LeftIcon, RightIcon, active, ...rest }) => {
    return (
        <button
            //@ts-expect-error spreading the rest props makes typescript upset, not really an issue
            type="button"
            data-active={active}
            className={classNames(
                "p-2 w-full flex gap-2.5 items-center rounded-lg data-[active=true]:text-white-800 hover:text-white-800 data-[active=true]:bg-white-100 hover:bg-white-100",
                className
            )}
            {...rest}
        >
            {!!LeftIcon && <Icons.SmallIcon className="text-white-400" Icon={LeftIcon} />}
            <span className="text-white-800 h5">{children}</span>
            {!!RightIcon && <Icons.SmallIcon className="text-white-400" Icon={RightIcon} />}
        </button>
    )
}

export const ButtonSpecial = ({
    onClick,
    title,
    className,
    background = "#121216", // gray-800
}: {
    onClick?: () => void
    title: string
    className?: string
    background?: string
}) => {
    // This component was intentionally made less reusable than other buttons for simplicity.
    // If we start using this elsewhere, we should make it more generic like other buttons in this file.
    const [hovered, setHovered] = React.useState(false)
    return (
        <div
            className={classNames(
                "relative overflow-hidden h-8 cursor-pointer rounded-lg",
                className
            )}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
        >
            <div className={classNames("absolute inset-0 bg-white-400 z-10")}>
                {/* DEFAULT BORDER */}
            </div>
            <div
                className={classNames("absolute inset-0 z-10 transition-transform", {
                    "-translate-x-full": !hovered,
                    "translate-x-0": hovered,
                })}
                style={{
                    background: "linear-gradient(90deg, #F1F43C 0%, #00AFFF 100%)",
                }}
            >
                {/* HOVER BORDER */}
            </div>
            <div
                className="absolute inset-px z-20 flex items-center justify-center gap-2 rounded-[7px]"
                style={{ background }}
            >
                <div className="w-3 h-[14px] relative">
                    <div
                        className={classNames("absolute inset-0 transition-opacity", {
                            "opacity-0": hovered,
                            "opacity-100": !hovered,
                        })}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="14"
                            viewBox="0 0 12 14"
                            fill="none"
                        >
                            <path
                                d="M5.72855 0.227116L4.61021 5.26893C4.58062 5.40004 4.48595 5.50463 4.36169 5.54053L0.206361 6.71591C-0.0687866 6.79396 -0.0687866 7.20604 0.206361 7.28409L4.36169 8.45947C4.48595 8.49381 4.58062 8.59839 4.61021 8.73107L5.72855 13.7729C5.79512 14.0757 6.20488 14.0757 6.27145 13.7729L7.38979 8.73107C7.41938 8.59995 7.51405 8.49537 7.63831 8.45947L11.7936 7.28409C12.0688 7.20604 12.0688 6.79396 11.7936 6.71591L7.63831 5.54053C7.51405 5.50619 7.41938 5.40161 7.38979 5.26893L6.27145 0.227116C6.20488 -0.0757052 5.79512 -0.0757052 5.72855 0.227116Z"
                                fill="url(#paint0_linear_7237_7835)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_7237_7835"
                                    x1="12"
                                    y1="14"
                                    x2="-2.24227"
                                    y2="12.7026"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stop-color="#00AFFF" />
                                    <stop offset="1" stop-color="#F1F43C" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <div
                        className={classNames("absolute inset-0 transition-opacity", {
                            "opacity-0": !hovered,
                            "opacity-100": hovered,
                        })}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="14"
                            viewBox="0 0 12 14"
                            fill="none"
                        >
                            <path
                                d="M5.72855 0.227116L4.61021 5.26893C4.58062 5.40004 4.48595 5.50463 4.36169 5.54053L0.206361 6.71591C-0.0687866 6.79396 -0.0687866 7.20604 0.206361 7.28409L4.36169 8.45947C4.48595 8.49381 4.58062 8.59839 4.61021 8.73107L5.72855 13.7729C5.79512 14.0757 6.20488 14.0757 6.27145 13.7729L7.38979 8.73107C7.41938 8.59995 7.51405 8.49537 7.63831 8.45947L11.7936 7.28409C12.0688 7.20604 12.0688 6.79396 11.7936 6.71591L7.63831 5.54053C7.51405 5.50619 7.41938 5.40161 7.38979 5.26893L6.27145 0.227116C6.20488 -0.0757052 5.79512 -0.0757052 5.72855 0.227116Z"
                                fill="#F1FF66"
                            />
                        </svg>
                    </div>
                </div>
                <div
                    className={classNames("transition-colors", {
                        "text-gray-100": !hovered,
                        "text-white": hovered,
                    })}
                >
                    {title}
                </div>
            </div>
        </div>
    )
}
